import "@/App.css";
import { Suspense, lazy as reactLazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { InstituteProvider } from "@/lib/institute";
import ErrorBoundary, { lazyRetry } from "@/components/ErrorBoundary";
const lazy = lazyRetry(reactLazy); // a stale deploy reloads once instead of leaving a white page
import ProtectedRoute from "@/components/ProtectedRoute";
import MarketingLayout from "@/layout/MarketingLayout";
import PwaBanners   from "@/components/PwaBanners";
import GradQuickAccess from "@/components/GradQuickAccess";
import BicepGuard, { RequireAdmin, RequireCoordinator, RequireMentor } from "@/components/BicepGuard";

// Route-level code splitting — previously every page in the app (portal
// booking, platform/superadmin admin tools, certificate verification,
// gallery, feedback, and every Graduation Day page) was statically
// imported into one monolithic bundle, meaning visiting ANY single route
// downloaded ALL of them. That's a real cost for the pages that matter
// most here: a student on mobile data opening /grad/confirm — a simple
// registration form — was downloading the entire admin panel, platform
// management tools, and every other unrelated page along with it, before
// the form even became interactive. CRA/webpack (this project's actual
// build, via craco — see package.json) splits each React.lazy() import
// into its own chunk automatically, no extra config needed, so this is
// safe to do broadly rather than a partial, inconsistent split.
const PlatformLogin      = lazy(() => import("@/pages/platform/PlatformLogin"));
const PlatformInstitutes = lazy(() => import("@/pages/platform/PlatformInstitutes"));
const AllowedUsers       = lazy(() => import("@/pages/superadmin/AllowedUsers"));
const Login        = lazy(() => import("@/pages/Login"));
const Portal        = lazy(() => import("@/pages/Portal"));
const Admin         = lazy(() => import("@/pages/Admin"));
const PublicEvents  = lazy(() => import("@/pages/PublicEvents"));
const EventDetail   = lazy(() => import("@/pages/EventDetail"));
const VerifyCert    = lazy(() => import("@/pages/VerifyCert"));
const CheckIn       = lazy(() => import("@/pages/CheckIn"));
const Gallery       = lazy(() => import("@/pages/Gallery"));
const MyEvents      = lazy(() => import("@/pages/MyEvents"));
const Feedback      = lazy(() => import("@/pages/Feedback"));
const Approve       = lazy(() => import("@/pages/Approve"));
const GradConfirm       = lazy(() => import("@/pages/grad/GradConfirm"));
const GradKioskDisplay  = lazy(() => import("@/pages/grad/GradKioskDisplay"));
const GradGuardian      = lazy(() => import("@/pages/grad/GradGuardian"));
const GradScan          = lazy(() => import("@/pages/grad/GradScan"));
const GradDashboard     = lazy(() => import("@/pages/grad/GradDashboard"));
const GradFormFields    = lazy(() => import("@/pages/grad/GradFormFields"));

// ── BICEP module (BMSIT-only, feature-flagged via inst.modules.bicep.enabled) ──
// BicepShell is BICEP's own platform layout (sidebar + topbar + Outlet).
// It is completely separate from the events Shell — BICEP runs as its own
// platform inside the SPA. Every /bicep/* route is a child of this layout.
const Shell                = lazy(() => import("@/layout/Shell"));
const BicepShell           = lazy(() => import("@/layout/BicepShell"));
// User-facing pages
const BicepHome            = lazy(() => import("@/pages/bicep/BicepHome"));
const BicepApply           = lazy(() => import("@/pages/bicep/BicepApply"));
const ProfilePanel          = lazy(() => import("@/components/ProfilePanel"));
const BicepGuide           = lazy(() => import("@/pages/bicep/BicepGuide"));
const ContactsAdmin        = lazy(() => import("@/pages/bicep/admin/ContactsAdmin"));
const BicepJoin            = lazy(() => import("@/pages/bicep/BicepJoin"));
const ApplicationView      = lazy(() => import("@/pages/bicep/ApplicationView"));
const ParticipantsAdmin    = lazy(() => import("@/pages/bicep/admin/ParticipantsAdmin"));
const MyApplications       = lazy(() => import("@/pages/bicep/MyApplications"));
const BicepEvents          = lazy(() => import("@/pages/bicep/BicepEvents"));
const BicepDashboard       = lazy(() => import("@/pages/bicep/BicepDashboard"));
const BicepSpaceRequests   = lazy(() => import("@/pages/bicep/BicepSpaceRequests"));
const BicepMentors         = lazy(() => import("@/pages/bicep/BicepMentors"));
const BicepResources       = lazy(() => import("@/pages/bicep/BicepResources"));
const BicepShowcase        = lazy(() => import("@/pages/bicep/BicepShowcase"));
const BicepCompetitions    = lazy(() => import("@/pages/bicep/BicepCompetitions"));
const BicepSchemes         = lazy(() => import("@/pages/bicep/BicepSchemes"));
const BicepCertificates    = lazy(() => import("@/pages/bicep/BicepCertificates"));
const BicepTeams           = lazy(() => import("@/pages/bicep/BicepTeams"));
const BicepLanding         = lazy(() => import("@/pages/bicep/BicepLanding"));
const BicepProblems        = lazy(() => import("@/pages/bicep/BicepProblems"));
const MentorWorkspace      = lazy(() => import("@/pages/bicep/MentorWorkspace"));
const BicepStartup         = lazy(() => import("@/pages/bicep/BicepStartup"));
const BicepNotifications   = lazy(() => import("@/pages/bicep/BicepNotifications"));
// Coordinator (programme) pages
const ManageHub            = lazy(() => import("@/pages/bicep/manage/ManageHub"));
const EventsAdmin          = lazy(() => import("@/pages/bicep/manage/EventsAdmin"));
const CompetitionsAdmin    = lazy(() => import("@/pages/bicep/manage/CompetitionsAdmin"));
const SchemesAdmin         = lazy(() => import("@/pages/bicep/manage/SchemesAdmin"));
const MentorsAdmin         = lazy(() => import("@/pages/bicep/manage/MentorsAdmin"));
const ResourcesAdmin       = lazy(() => import("@/pages/bicep/manage/ResourcesAdmin"));
const ProblemsAdmin        = lazy(() => import("@/pages/bicep/manage/ProblemsAdmin"));
// Admin (whole-process) pages
const BicepAdminHub        = lazy(() => import("@/pages/bicep/admin/BicepAdminHub"));
const ApplicationsReview   = lazy(() => import("@/pages/bicep/admin/ApplicationsReview"));
const ApplicationDetail    = lazy(() => import("@/pages/bicep/admin/ApplicationDetail"));
const IncubateesAdmin      = lazy(() => import("@/pages/bicep/admin/IncubateesAdmin"));
const SpaceRequestsAdmin   = lazy(() => import("@/pages/bicep/admin/SpaceRequestsAdmin"));
const FundingAdmin         = lazy(() => import("@/pages/bicep/admin/FundingAdmin"));
const ProgressAdmin        = lazy(() => import("@/pages/bicep/admin/ProgressAdmin"));
const CommsAdmin           = lazy(() => import("@/pages/bicep/admin/CommsAdmin"));
const PeopleAdmin          = lazy(() => import("@/pages/bicep/admin/PeopleAdmin"));
const SettingsAdmin        = lazy(() => import("@/pages/bicep/admin/SettingsAdmin"));

const Departments   = lazy(() => import("@/pages/superadmin/Departments"));
const Approvals     = lazy(() => import("@/pages/superadmin/Approvals"));
const Faq           = lazy(() => import("@/pages/superadmin/Faq"));
const Home          = lazy(() => import("@/pages/marketing/Home"));
const Features      = lazy(() => import("@/pages/marketing/Features"));
const HowItWorks    = lazy(() => import("@/pages/marketing/HowItWorks"));
const Contact       = lazy(() => import("@/pages/marketing/Contact"));

// Shown only for the moment a chunk is actually downloading — on a fast
// connection this is barely visible; on a slow one it's the difference
// between a blank white screen and something that reads as "still
// loading", not "broken".
function RouteFallback() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#faf7f0" }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #e5e0d5", borderTopColor: "#1346E8", animation: "app-route-spin 0.8s linear infinite" }} />
      <style>{"@keyframes app-route-spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

export default function App() {
  return (
    <InstituteProvider>
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* ── Platform admin (manages the institute registry itself —
                separate login from any institute's own admin) ──────────── */}
            <Route path="/platform"             element={<PlatformLogin />} />
            <Route path="/platform/institutes"  element={<PlatformInstitutes />} />

            {/* ── Public marketing site (Solution-style, Arventiq-branded) ─ */}
            <Route element={<MarketingLayout />}>
              <Route path="/"              element={<Home />} />
              <Route path="/features"      element={<Features />} />
              <Route path="/how-it-works"  element={<HowItWorks />} />
              <Route path="/contact"       element={<Contact />} />
            </Route>

            {/* ── Public — no login ─────────────────────────────────────── */}
            <Route path="/login"             element={<Login />} />
            <Route path="/events"            element={<PublicEvents />} />
            <Route path="/events/:id"        element={<EventDetail />} />
            {/* Obfuscated public event/ticket URL used by confirmation emails:
                /6b345a51df60fed6f07c5ff42e1853d0?id=EVT-005&reg=…&token=…
                No :id path param here — EventDetail falls back to ?id= in the query. */}
            <Route path="/6b345a51df60fed6f07c5ff42e1853d0" element={<EventDetail />} />
            {/* verify.html */}
            <Route path="/verify"            element={<VerifyCert />} />
            {/* Obfuscated public certificate URL used by certificate emails:
                /80dbb0e1d57f37962da39c535a27a015?token=… */}
            <Route path="/80dbb0e1d57f37962da39c535a27a015" element={<VerifyCert />} />
            {/* my-events.html */}
            <Route path="/my-events"         element={<MyEvents />} />
            {/* feedback.html */}
            <Route path="/feedback"          element={<Feedback />} />
            {/* gallery-upload.html — photographer portal (OTP-gated inside component) */}
            <Route path="/gallery-upload"    element={<Gallery />} />
            {/* checkin.html — OTP-gated inside component itself */}
            <Route path="/checkin"           element={<CheckIn />} />
            {/* HOD approval landing (secure token in ?atk=…, no login needed) */}
            <Route path="/approve"           element={<Approve />} />

            <Route path="/profile"           element={<ProtectedRoute><Shell role="portal" title="My profile"><ProfilePanel /></Shell></ProtectedRoute>} />

            {/* ── Graduation Day module (v7.9) — new, isolated routes ──── */}
            {/* Public — student confirms attendance via email OTP */}
            <Route path="/grad/confirm"      element={<GradConfirm />} />
            <Route path="/grad/display"      element={<GradKioskDisplay />} />
            {/* Public — guardian registers against a student USN */}
            <Route path="/grad/guardian"     element={<GradGuardian />} />
            {/* Staff — QR scanner + walk-in desk. Access is event-scoped
                (Host/Coordinator via event_team), not platform-role-scoped —
                any logged-in user can reach the route, the page itself
                checks gradApi.getMyAccess() and shows a clean denial if
                they're not on the Graduation Day team. */}
            <Route path="/grad/scan"         element={<ProtectedRoute><GradScan /></ProtectedRoute>} />
            {/* Staff — overview dashboard, reports, roster import, team management */}
            <Route path="/grad/dashboard"    element={<ProtectedRoute><GradDashboard /></ProtectedRoute>} />
            {/* Hosts + superadmin — manage configurable fields (page-level gate) */}
            <Route path="/grad/fields"       element={<ProtectedRoute><GradFormFields /></ProtectedRoute>} />

            {/* ── BICEP module — BMSIT only, feature-flagged ───────────── */}
            {/*
             * LAYOUT ROUTE PATTERN:
             *   BicepGuard  →  checks token + bicepGetInfo (enabled + role) once
             *     BicepShell  →  BICEP's own sidebar + topbar + <Outlet />
             *       <page>    →  renders inside Outlet (no Shell wrapper on pages)
             *
             * Every /bicep/* page has had its <Shell role="portal"> wrapper
             * removed. BicepShell provides the full layout via React Router Outlet.
             * "← Back to Events Platform" in the sidebar returns to /portal.
             */}

            {/* Public BICEP landing page — no sign-in needed */}
            <Route path="/about-bicep"           element={<BicepLanding />} />

            {/* One layout route: BicepGuard fetches the caller's role once and
                provides it to BicepShell + pages. Coordinator pages are gated
                individually with <RequireCoordinator>, so moving between user
                and admin pages never remounts the shell. */}
            <Route element={<BicepGuard><BicepShell /></BicepGuard>}>
              <Route path="/bicep"                  element={<BicepHome />} />
              <Route path="/bicep/apply"            element={<BicepApply />} />
              <Route path="/bicep/profile"          element={<ProfilePanel />} />
              <Route path="/bicep/guide"            element={<BicepGuide />} />
              <Route path="/bicep/join"             element={<BicepJoin />} />
              <Route path="/bicep/applications/:id" element={<ApplicationView />} />
              <Route path="/bicep/my-applications"  element={<MyApplications />} />
              <Route path="/bicep/startup"          element={<BicepStartup />} />
              <Route path="/bicep/notifications"    element={<BicepNotifications />} />
              <Route path="/bicep/events"           element={<BicepEvents />} />
              <Route path="/bicep/dashboard"        element={<BicepDashboard />} />
              <Route path="/bicep/space"            element={<BicepSpaceRequests />} />
              <Route path="/bicep/mentors"          element={<BicepMentors />} />
              <Route path="/bicep/resources"        element={<BicepResources />} />
              <Route path="/bicep/showcase"         element={<BicepShowcase />} />
              <Route path="/bicep/competitions"     element={<BicepCompetitions />} />
              <Route path="/bicep/schemes"          element={<BicepSchemes />} />
              <Route path="/bicep/certificates"     element={<BicepCertificates />} />
              <Route path="/bicep/teams"            element={<BicepTeams />} />
              <Route path="/bicep/problems"         element={<BicepProblems />} />

              {/* Mentors — the students assigned to them */}
              <Route path="/bicep/mentor"                   element={<RequireMentor><MentorWorkspace /></RequireMentor>} />

              {/* Coordinators (and admins) — the programmes students take part in */}
              <Route path="/bicep/manage"                   element={<RequireCoordinator><ManageHub /></RequireCoordinator>} />
              <Route path="/bicep/manage/events"            element={<RequireCoordinator><EventsAdmin /></RequireCoordinator>} />
              <Route path="/bicep/manage/competitions"      element={<RequireCoordinator><CompetitionsAdmin /></RequireCoordinator>} />
              <Route path="/bicep/manage/schemes"           element={<RequireCoordinator><SchemesAdmin /></RequireCoordinator>} />
              <Route path="/bicep/manage/mentors"           element={<RequireCoordinator><MentorsAdmin /></RequireCoordinator>} />
              <Route path="/bicep/manage/resources"         element={<RequireCoordinator><ResourcesAdmin /></RequireCoordinator>} />
              <Route path="/bicep/manage/problems"          element={<RequireCoordinator><ProblemsAdmin /></RequireCoordinator>} />

              {/* BICEP admins — the whole process */}
              <Route path="/bicep/admin"                    element={<RequireAdmin><BicepAdminHub /></RequireAdmin>} />
              <Route path="/bicep/admin/applications"       element={<RequireAdmin><ApplicationsReview /></RequireAdmin>} />
              <Route path="/bicep/admin/applications/:id"   element={<RequireAdmin><ApplicationDetail /></RequireAdmin>} />
              <Route path="/bicep/admin/contacts"           element={<RequireAdmin><ContactsAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/participants"       element={<RequireAdmin><ParticipantsAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/incubatees"         element={<RequireAdmin><IncubateesAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/space"              element={<RequireAdmin><SpaceRequestsAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/funding"            element={<RequireAdmin><FundingAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/progress"           element={<RequireAdmin><ProgressAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/comms"              element={<RequireAdmin><CommsAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/people"             element={<RequireAdmin><PeopleAdmin /></RequireAdmin>} />
              <Route path="/bicep/admin/settings"           element={<RequireAdmin><SettingsAdmin /></RequireAdmin>} />
              {/* Old URLs */}
              <Route path="/bicep/admin/legacy"             element={<Navigate to="/bicep/admin" replace />} />
              <Route path="/bicep/admin/mentorship"         element={<Navigate to="/bicep/manage/mentors" replace />} />
              <Route path="/bicep/admin/competitions"       element={<Navigate to="/bicep/manage/competitions" replace />} />
              <Route path="/bicep/admin/content"            element={<Navigate to="/bicep/admin/comms" replace />} />
            </Route>

            {/* ── Authenticated — any logged-in user ────────────────────── */}
            <Route path="/portal/*"          element={<ProtectedRoute><Portal /></ProtectedRoute>} />

            {/* ── HOD approval admin (superadmin manages depts; both review) ── */}
            <Route path="/admin/departments" element={<ProtectedRoute roles={["superadmin"]}><Departments /></ProtectedRoute>} />
            <Route path="/admin/approvals"   element={<ProtectedRoute roles={["superadmin","incharge"]}><Approvals /></ProtectedRoute>} />
            <Route path="/admin/faq"         element={<ProtectedRoute roles={["superadmin"]}><Faq /></ProtectedRoute>} />
            <Route path="/admin/users"       element={<ProtectedRoute roles={["superadmin"]}><AllowedUsers /></ProtectedRoute>} />

            {/* ── Admin — incharge or superadmin ────────────────────────── */}
            {/* Obfuscated URL matching the HTML version */}
            <Route path="/admin"             element={<ProtectedRoute roles={["superadmin","incharge"]}><Admin /></ProtectedRoute>} />
            <Route path="/admin/*"           element={<ProtectedRoute roles={["superadmin","incharge"]}><Admin /></ProtectedRoute>} />
            {/* The HTML version uses /8feb79055fe6f9e2641c4f17cb852393 for admin */}
            <Route path="/8feb79055fe6f9e2641c4f17cb852393"
                                             element={<ProtectedRoute roles={["superadmin","incharge"]}><Admin /></ProtectedRoute>} />

            {/* Fallback → public marketing home */}
            <Route path="*"                  element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          {/* Inside BrowserRouter, unlike PwaBanners below — this one uses
              useLocation()/<Link>, which only work within the router. */}
          <GradQuickAccess />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <PwaBanners />
      </div>
    </AuthProvider>
    </InstituteProvider>
  );
}
